import { motion, Variants } from "framer-motion";
import { useAtomValue } from "jotai";
import Image from "next/image";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";

import { sequentialLoadingState } from "~/atoms/sequential-loading";
import { useBreakpoint } from "~/contexts/breakpoint";
import CrossCountry from "~/types/cross-country";
import Constants from "~/utils/constants";
import imageUtils from "~/utils/image-utils";
import productUtils from "~/utils/product-utils";
import TrackingUtils from "~/utils/tracking-utils";

import Container from "../common/container";
import Grid from "../common/grid";
import Pane from "../common/pane";
import ProductActions from "../common/product-actions";
import NotifyMe from "../notify-me/notify-me";
import AccordionShadeSelector from "./accordion-shade-selector";
import styles from "./product-fixed-pane.module.scss";
import ProductStackedPrice from "./product-stacked-price";

const paneVariants: Variants = {
  visible: {
    opacity: 1,
    y: "0%",
    visibility: "visible",
  },
  hidden: {
    opacity: 0,
    y: "50%",
    visibility: "hidden",
  },
};

type Props = {
  root: CrossCountry.DetailedProduct;
  showFixedPanel: boolean;
  selectedShade: CrossCountry.ChildrenProduct;
  shades: CrossCountry.Product[];
  shadesOOS?: Record<string, boolean>;
  isLoadingAvailability?: boolean;
};

export default function ProductFixedPane(props: Props) {
  const refNotifyMe = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const breakpoint = useBreakpoint();
  const goccia = productUtils.getProductDroplet(props.selectedShade?.product_media?.media);

  const [usableNetButton, setUsableNetButton] = useState<HTMLElement | null>(null);
  const [usableNetPosition, setUsableNetPosition] = useState({
    initialPanelHeight: 0,
    shadesPanelHeight: 0,
    isPanelOpen: false,
  });

  const [productRootId, setProductRootId] = useState<string | undefined>(undefined);
  const [isNotifyMeOpen, setIsNotifyMeOpen] = useState<boolean>(false);
  const sequentialLoadingReady = useAtomValue(sequentialLoadingState);

  const URLRef = useRef<string | undefined>(router.asPath);
  const panelHeightRef = useRef<HTMLDivElement>(null);

  // Reset the notify me panel open state when the URL changes
  useEffect(() => {
    if (router.asPath !== undefined && URLRef.current && URLRef.current !== router.asPath) {
      setIsNotifyMeOpen(false);
      URLRef.current = router.asPath;
    }
  }, [setIsNotifyMeOpen, router.asPath]);

  const upadtePanel = (open: boolean) => {
    setUsableNetPosition((prev) => ({ ...prev, isPanelOpen: open }));
  };

  useEffect(() => {
    const usableNetContainer = document.getElementById(Constants.USABLENET_CONTAINER_KEY);
    if (sequentialLoadingReady.usablenet && usableNetContainer) {
      setUsableNetButton(usableNetContainer);
    }
  }, [sequentialLoadingReady.usablenet]);

  useEffect(() => {
    if (props.root.product_id !== productRootId) {
      setProductRootId(props.root.product_id);

      setTimeout(() => {
        const panel = panelHeightRef.current;
        const panelHeight = panel?.offsetHeight || 0;
        const shadesPanel =
          props.shades && props.shades.length > 1 ? document.getElementById(Constants.PDP_SHADES_PANEL) : null;
        const shadesPanelHeight = shadesPanel?.offsetHeight || 0;

        const initialPanelHeight = usableNetPosition.isPanelOpen ? panelHeight - shadesPanelHeight - 33 : panelHeight;

        setUsableNetPosition((prev) => ({
          ...prev,
          initialPanelHeight: initialPanelHeight + 25,
          shadesPanelHeight: shadesPanelHeight + 30,
        }));
      }, 300);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productRootId, props.root.product_id]);

  useEffect(() => {
    if (usableNetButton) {
      if (props.showFixedPanel && breakpoint === "mobile") {
        const finalHeight =
          usableNetPosition.initialPanelHeight +
          (usableNetPosition.isPanelOpen ? usableNetPosition.shadesPanelHeight : 0);

        usableNetButton.style.bottom = `clamp(20px, calc(${finalHeight}px), calc(100vh - ${Constants.HEADER_HEIGHT_MOBILE}px))`;
      } else {
        usableNetButton.style.bottom = `20px`;
      }
    }

    return () => {
      if (usableNetButton) {
        usableNetButton.style.bottom = "";
      }
    };
  }, [breakpoint, props.showFixedPanel, usableNetButton, usableNetPosition]);

  return (
    <motion.div
      className={styles.fixedContainer}
      variants={paneVariants}
      initial={props.showFixedPanel ? "visible" : "hidden"}
      animate={props.showFixedPanel ? "visible" : "hidden"}
      aria-hidden
    >
      <Container>
        <Grid>
          <Pane ref={panelHeightRef} className={styles.fixedPane}>
            {props.shades && props.shades.length > 1 && (
              <AccordionShadeSelector
                version="v1"
                root={props.root}
                selectedShade={props.selectedShade}
                shades={props.shades}
                shadesOOS={props.shadesOOS}
                hideAccordion={isNotifyMeOpen}
                onAccordionChange={upadtePanel}
              />
            )}
            <div className={styles.purchaseInfo}>
              {/* Notify Me Form */}
              {isNotifyMeOpen && (
                <>
                  <div className={styles.shadesHeader}>
                    {goccia ? (
                      <Image
                        className={styles.shadeImage}
                        alt={props.selectedShade.display_value}
                        src={goccia.url}
                        height={16}
                        width={16}
                        loader={imageUtils.evaImageLoader}
                      />
                    ) : null}
                    <p className={styles.shadeColor}>{props.selectedShade?.color}</p>
                  </div>
                  <NotifyMe
                    ref={refNotifyMe}
                    className={styles.notifyMeForm}
                    productId={props.selectedShade.product_id}
                    onClose={() => setIsNotifyMeOpen(false)}
                    showSubmitButton={false}
                    showCloseIcon
                  />
                </>
              )}

              <div className={styles.actionsContainer}>
                <ProductStackedPrice product={props.selectedShade} />

                <ProductActions
                  hasPrice={productUtils.hasValidDisplayPrice(props.selectedShade)}
                  productId={props.selectedShade.product_id}
                  outOfStock={props.isLoadingAvailability ? false : !props.selectedShade.has_stock}
                  isLoading={props.isLoadingAvailability}
                  onClickNotifyMe={() => {
                    if (!isNotifyMeOpen) {
                      return setIsNotifyMeOpen(true);
                    }

                    // if notify me is open, clicking on the button submits the form contained <NotifyMe>
                    refNotifyMe.current
                      ?.querySelector("form")
                      ?.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }));
                  }}
                  actionsContext="pdp"
                  trackingData={TrackingUtils.mapEcomItem(props.root, props.selectedShade, {})}
                  maxQuantity={props.selectedShade.ecomm_max_items_per_order}
                />
              </div>
            </div>
          </Pane>
        </Grid>
      </Container>
    </motion.div>
  );
}
